/**
 * Parts Catalog Service Layer
 * Handles all Firestore operations for the parts_catalog collection
 * Master parts database for presite selection, provisioning, and quoting
 *
 * UPDATED: Now supports both Deal Workspace (theo-workflow) and Playground quoting use cases
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  increment,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { PartsCatalogItem, BundleChildItem, PlaygroundQuoteItem } from '@/types';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { stripUndefinedDeep } from '@/utils/firestore';
import { getLocaleSync } from '@/utils/locale';
import { roundMoney, toFiniteMoney } from '@shared/money/round';

const COLLECTION_NAME = 'parts_catalog';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * SKUs are user-typed and can have weird casing/whitespace. We DO NOT
 * mutate the stored SKU (existing parts_catalog data was written without
 * normalisation), but we compare case-insensitively at lookup time.
 */
function normaliseSkuForCompare(sku: string | undefined | null): string {
  return (sku ?? '').trim().toUpperCase();
}

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new parts catalog item
 */
export async function createPartsCatalogItem(
  data: Omit<PartsCatalogItem, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const itemData: Omit<PartsCatalogItem, 'id'> = {
    ...data,
    isActive: data.isActive !== undefined ? data.isActive : true,
    // Default `usageCount: 0` so the doc is visible to queries that
    // `orderBy('usageCount')` (e.g. `getPopularParts`). Firestore
    // silently excludes documents missing the orderBy field — without
    // this default, newly uploaded parts wouldn't show in the
    // proposal builder's "popular" landing view until they were
    // searched for by name.
    usageCount: data.usageCount ?? 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Firestore rejects `undefined` values entirely. Callers commonly
  // pass `undefined` for optional fields (sku, costPrice, supplier,
  // etc.) — strip them before write so the doc has no undefined
  // leaves. `null` is preserved (distinct meaning) and FieldValue
  // sentinels are preserved (prototype check inside the helper).
  const clean = stripUndefinedDeep(itemData) as Omit<PartsCatalogItem, 'id'>;

  const docRef = await addDoc(collection(db, COLLECTION_NAME), clean);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single parts catalog item by ID
 */
export async function getPartsCatalogItem(
  partId: string
): Promise<PartsCatalogItem | null> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PartsCatalogItem;
}

/**
 * Get all parts catalog items for the current tenant.
 *
 * Filters by `tenantId` using the legacy-alias union so pre-migration
 * docs still surface. Without this filter, a multi-tenant deployment
 * would expose every tenant's parts catalog (pricing, suppliers,
 * inventory) to every signed-in user — see docs/MULTI_TENANT_AUDIT.md
 * §4.2.
 */
export async function getAllPartsCatalogItems(
  tenantId: string = getTenantIdSync()
): Promise<PartsCatalogItem[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PartsCatalogItem
  );
}

/**
 * Get only active parts catalog items (not archived).
 *
 * Tenant filter is required — `parts_catalog` is a tenant-isolated
 * collection (Phase 0.5 Session A) and an un-filtered query fails
 * the rule check with "Missing or insufficient permissions".
 * `tenantIdReadUnion()` keeps legacy-alias docs visible during the
 * migration window.
 */
export async function getActivePartsCatalogItems(): Promise<PartsCatalogItem[]> {
  // Query all tenant parts, then filter out archived ones client-side
  // (Firestore doesn't support where('archived', '!=', true) with
  // orderBy on a different field).
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PartsCatalogItem
    )
    .filter((part) => !part.archived); // Filter out archived parts
}

/**
 * Get parts catalog items by category (not archived).
 *
 * Tenant filter is required for the same reason as
 * `getActivePartsCatalogItems` above — `parts_catalog` is
 * tenant-isolated and unfiltered reads fail the rule check.
 */
export async function getPartsCatalogItemsByCategory(
  category: string
): Promise<PartsCatalogItem[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('category', '==', category),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PartsCatalogItem
    )
    .filter((part) => !part.archived); // Filter out archived parts
}

/**
 * Search parts catalog by name, SKU, supplier, or supplierSku (client-side filtering)
 */
export async function searchPartsCatalogByName(
  searchTerm: string
): Promise<PartsCatalogItem[]> {
  const allParts = await getActivePartsCatalogItems();
  const lowerSearch = searchTerm.toLowerCase();

  return allParts.filter(
    (part) =>
      part.name.toLowerCase().includes(lowerSearch) ||
      part.sku?.toLowerCase().includes(lowerSearch) ||
      part.description?.toLowerCase().includes(lowerSearch) ||
      part.supplier?.toLowerCase().includes(lowerSearch) ||
      part.supplierSku?.toLowerCase().includes(lowerSearch)
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a parts catalog item.
 *
 * Strips `undefined` values from the payload before writing — Firestore
 * rejects `undefined` outright with "Unsupported field value: undefined",
 * which would otherwise blow up the whole update whenever an optional
 * field (e.g. `supplierSku`, `brand`) was never set on the source doc.
 * Callers who want to clear a field should pass `null` (translated to
 * `deleteField()`) or an empty string instead.
 */
export async function updatePartsCatalogItem(
  partId: string,
  data: Partial<Omit<PartsCatalogItem, 'id' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);

  const payload: Record<string, unknown> = {
    updatedAt: new Date().toISOString(),
  };
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    payload[key] = value === null ? deleteField() : value;
  }

  await updateDoc(docRef, payload);
}

/**
 * Bulk edit helper for the five Projects v2 §4.7 cost-critical fields on a
 * single part. `null` clears the field via `deleteField()`; `undefined` skips
 * it; a number writes that value (including `0`).
 *
 * Used by the Bulk Edit Costs CSV flow (P0.3) so the five fields can be
 * round-tripped through Excel without the caller having to import Firestore
 * primitives.
 */
export type PartsCatalogCostFieldUpdate = {
  installLabourMinutes?: number | null;
  terminationCostEach?: number | null;
  terminationSellEach?: number | null;
  pullLabourMinPerMeter?: number | null;
  wastageFactorDefault?: number | null;
};

export async function updatePartsCatalogCostFields(
  partId: string,
  fields: PartsCatalogCostFieldUpdate
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);

  const payload: Record<string, unknown> = {
    updatedAt: new Date().toISOString(),
  };

  const keys: (keyof PartsCatalogCostFieldUpdate)[] = [
    'installLabourMinutes',
    'terminationCostEach',
    'terminationSellEach',
    'pullLabourMinPerMeter',
    'wastageFactorDefault',
  ];

  for (const key of keys) {
    const v = fields[key];
    if (v === undefined) continue; // skip (no change)
    payload[key] = v === null ? deleteField() : v;
  }

  await updateDoc(docRef, payload);
}

/**
 * Deactivate a parts catalog item (soft delete)
 */
export async function deactivatePartsCatalogItem(partId: string): Promise<void> {
  await updatePartsCatalogItem(partId, { isActive: false });
}

/**
 * Reactivate a parts catalog item
 */
export async function reactivatePartsCatalogItem(partId: string): Promise<void> {
  await updatePartsCatalogItem(partId, { isActive: true });
}

/**
 * Archive a parts catalog item (soft delete with archived flag)
 */
export async function archivePart(partId: string, userId?: string): Promise<void> {
  await updatePartsCatalogItem(partId, {
    archived: true,
    archivedAt: new Date().toISOString(),
    archivedBy: userId,
    isActive: false,
  });
}

/**
 * Unarchive a parts catalog item
 */
export async function unarchivePart(partId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  await updateDoc(docRef, {
    archived: false,
    archivedAt: deleteField(),
    archivedBy: deleteField(),
    isActive: true,
    updatedAt: new Date().toISOString(),
  });
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a parts catalog item (hard delete)
 * WARNING: This will break references in presite_parts if they reference this part
 */
export async function deletePartsCatalogItem(partId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  await deleteDoc(docRef);
}

// ============================================================================
// SEED DATA (Optional - for initial setup)
// ============================================================================

/**
 * Seed the parts catalog with common items
 * Call this once to populate the database
 */
export async function seedPartsCatalog(): Promise<void> {
  const seedParts: Omit<PartsCatalogItem, 'id' | 'createdAt' | 'updatedAt'>[] = [
    // Networking & Internet
    {
      name: 'Starlink Dish (Gen 2)',
      sku: 'SL-DISH-G2',
      category: 'Networking',
      description: 'Starlink satellite dish, 2nd generation',
      price: 599.0,
      isActive: true,
    },
    {
      name: 'Starlink Router',
      sku: 'SL-ROUTER',
      category: 'Networking',
      description: 'Starlink Wi-Fi router',
      price: 99.0,
      isActive: true,
    },
    {
      name: 'Wi-Fi 6 Router',
      sku: 'WIFI6-RTR',
      category: 'Networking',
      description: 'High-performance Wi-Fi 6 router',
      price: 199.0,
      isActive: true,
    },
    {
      name: 'Modem (Cable)',
      sku: 'MDM-CABLE',
      category: 'Networking',
      description: 'DOCSIS 3.1 cable modem',
      price: 129.0,
      isActive: true,
    },
    {
      name: 'Network Switch (8-port)',
      sku: 'SWITCH-8P',
      category: 'Networking',
      description: 'Gigabit Ethernet switch, 8 ports',
      price: 79.0,
      isActive: true,
    },
    {
      name: 'Network Switch (16-port)',
      sku: 'SWITCH-16P',
      category: 'Networking',
      description: 'Gigabit Ethernet switch, 16 ports',
      price: 149.0,
      isActive: true,
    },
    {
      name: 'Mesh Wi-Fi Node',
      sku: 'MESH-NODE',
      category: 'Networking',
      description: 'Mesh Wi-Fi extender node',
      price: 89.0,
      isActive: true,
    },

    // Cabling & Accessories
    {
      name: 'Ethernet Cable (Cat6, 50ft)',
      sku: 'ETH-CAT6-50',
      category: 'Cabling',
      description: 'Cat6 Ethernet cable, 50 feet',
      price: 15.0,
      isActive: true,
    },
    {
      name: 'Ethernet Cable (Cat6, 100ft)',
      sku: 'ETH-CAT6-100',
      category: 'Cabling',
      description: 'Cat6 Ethernet cable, 100 feet',
      price: 25.0,
      isActive: true,
    },
    {
      name: 'Coaxial Cable (50ft)',
      sku: 'COAX-50',
      category: 'Cabling',
      description: 'RG6 coaxial cable, 50 feet',
      price: 18.0,
      isActive: true,
    },
    {
      name: 'Ethernet Adapter (USB-C to Ethernet)',
      sku: 'ETH-ADPT-USBC',
      category: 'Cabling',
      description: 'USB-C to Gigabit Ethernet adapter',
      price: 29.0,
      isActive: true,
    },

    // Smart Home Devices
    {
      name: 'Smart Hub (Google Nest)',
      sku: 'HUB-GOOGLE',
      category: 'Smart Home',
      description: 'Google Nest Hub smart display',
      price: 99.0,
      isActive: true,
    },
    {
      name: 'Smart Hub (Amazon Echo)',
      sku: 'HUB-ALEXA',
      category: 'Smart Home',
      description: 'Amazon Echo smart speaker',
      price: 89.0,
      isActive: true,
    },
    {
      name: 'Smart Light Switch',
      sku: 'SWITCH-LIGHT',
      category: 'Smart Home',
      description: 'Wi-Fi enabled light switch',
      price: 49.0,
      isActive: true,
    },
    {
      name: 'Smart Bulb (Color)',
      sku: 'BULB-COLOR',
      category: 'Smart Home',
      description: 'RGB color smart LED bulb',
      price: 35.0,
      isActive: true,
    },
    {
      name: 'Smart Bulb (White)',
      sku: 'BULB-WHITE',
      category: 'Smart Home',
      description: 'Dimmable white smart LED bulb',
      price: 25.0,
      isActive: true,
    },
    {
      name: 'Smart Thermostat',
      sku: 'THERMO-SMART',
      category: 'Smart Home',
      description: 'Wi-Fi programmable thermostat',
      price: 179.0,
      isActive: true,
    },
    {
      name: 'Smart Plug',
      sku: 'PLUG-SMART',
      category: 'Smart Home',
      description: 'Wi-Fi enabled smart outlet',
      price: 19.0,
      isActive: true,
    },
    {
      name: 'Door/Window Sensor',
      sku: 'SENSOR-DOOR',
      category: 'Smart Home',
      description: 'Wireless door/window contact sensor',
      price: 29.0,
      isActive: true,
    },
    {
      name: 'Motion Sensor',
      sku: 'SENSOR-MOTION',
      category: 'Smart Home',
      description: 'Wireless motion detector',
      price: 35.0,
      isActive: true,
    },

    // Cameras & Security
    {
      name: 'Indoor Camera',
      sku: 'CAM-INDOOR',
      category: 'Security',
      description: 'Indoor Wi-Fi security camera, 1080p',
      price: 79.0,
      isActive: true,
    },
    {
      name: 'Outdoor Camera',
      sku: 'CAM-OUTDOOR',
      category: 'Security',
      description: 'Weatherproof outdoor camera, 1080p',
      price: 129.0,
      isActive: true,
    },
    {
      name: 'Doorbell Camera',
      sku: 'CAM-DOORBELL',
      category: 'Security',
      description: 'Video doorbell with 2-way audio',
      price: 149.0,
      isActive: true,
    },

    // Installation Hardware
    {
      name: 'Mounting Pole',
      sku: 'MOUNT-POLE',
      category: 'Hardware',
      description: 'Adjustable mounting pole for dishes/antennas',
      price: 45.0,
      isActive: true,
    },
    {
      name: 'Wall Mount Bracket',
      sku: 'MOUNT-WALL',
      category: 'Hardware',
      description: 'Universal wall mount bracket',
      price: 25.0,
      isActive: true,
    },
    {
      name: 'Cable Management Kit',
      sku: 'CABLE-KIT',
      category: 'Hardware',
      description: 'Cable clips, ties, and conduit',
      price: 29.0,
      isActive: true,
    },
    {
      name: 'Wire Connectors (100pk)',
      sku: 'WIRE-CONN',
      category: 'Hardware',
      description: 'Assorted wire nuts and connectors',
      price: 15.0,
      isActive: true,
    },
    {
      name: 'Power Supply (12V)',
      sku: 'PSU-12V',
      category: 'Hardware',
      description: '12V DC power supply adapter',
      price: 19.0,
      isActive: true,
    },
  ];

  const promises = seedParts.map((part) => createPartsCatalogItem(part));
  await Promise.all(promises);

  console.log(`[PARTS CATALOG] Seeded ${seedParts.length} parts`);
}

// ============================================================================
// PLAYGROUND QUOTING ENHANCEMENTS
// ============================================================================

/**
 * Increment usage count when part is used in a quote
 * Tracks popularity for "Most Used" sorting
 */
export async function incrementPartUsage(partId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  await updateDoc(docRef, {
    usageCount: increment(1),
    lastUsed: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Get most popular parts (sorted by usage count, not archived).
 * Used by the proposal builder's "Add from library" picker as the
 * landing view.
 *
 * Why we sort client-side instead of letting Firestore do it:
 *   Firestore's `orderBy('usageCount', 'desc')` SILENTLY EXCLUDES
 *   documents that lack the `usageCount` field. Pre-Step-6 uploads
 *   (BulkAddTab CSV imports, etc.) didn't default the field, so
 *   those parts never showed up in the picker until the user hunted
 *   for them by name. Sorting client-side keeps every tenant doc
 *   visible and treats missing-field docs as `usageCount: 0` (they
 *   sort to the end of the list).
 */
export async function getPopularParts(
  limitCount: number = 10,
  tenantId: string = getTenantIdSync()
): Promise<PartsCatalogItem[]> {
  // `parts_catalog` is tenant-isolated — unfiltered reads fail the
  // rule check. Union-read for legacy-alias compat. No `orderBy`
  // here — see the docstring above.
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    // Pull a generous slice — usage counts are typically a long-tail
    // distribution and we want to make sure new uploads (usageCount=0
    // or missing) still appear in the picker. The client-side sort
    // + slice clamp the displayed set.
    firestoreLimit(Math.max(limitCount * 4, 50)),
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }) as PartsCatalogItem)
    .filter((part) => !part.archived)
    // Sort by usageCount desc, treating missing as 0. Names break
    // ties so the order is stable across reads.
    .sort((a, b) => {
      const ua = a.usageCount ?? 0;
      const ub = b.usageCount ?? 0;
      if (ub !== ua) return ub - ua;
      return (a.name ?? '').localeCompare(b.name ?? '');
    })
    .slice(0, limitCount);
}

/**
 * Save a PlaygroundQuoteItem to the parts catalog
 * Allows users to save custom quote items for reuse
 */
export async function saveQuoteItemToCatalog(
  item: PlaygroundQuoteItem,
  tenantId?: string,
  userId?: string,
  userName?: string,
  category?: string,
  brand?: string
): Promise<string> {
  const catalogItem: Omit<PartsCatalogItem, 'id' | 'createdAt' | 'updatedAt'> = {
    name: item.name,
    sku: item.sku,
    category: category || item.category,
    brand: brand,
    description: item.notes || `Saved from quote on ${new Date().toLocaleDateString(getLocaleSync())}`,
    price: item.unitPrice,
    isActive: true,
    metadata: {
      unitCost: item.unitCost,
      markup: item.markup,
      unit: item.unit,
      savedFromQuoteItemId: item.id,
      savedBy: userId,
      savedByName: userName,
    },
    tenantId,
    usageCount: 0,
    lastUsed: new Date().toISOString(),
  };

  return createPartsCatalogItem(catalogItem);
}

/**
 * Convert a catalog part to a PlaygroundQuoteItem
 * For adding catalog items to quotes
 *
 * ⚠️ Known price drift — deliberately NOT fixed here because existing
 * callers depend on these quirks (docs/proposal-v2/S1.md §3.8, plan
 * non-negotiable 8): sell price reads `price` only (a part that only has
 * `sellPrice` quotes at 0); cost reads `metadata.unitCost || price`
 * (ignores `costPrice`, so cost usually equals sell and margin reads 0);
 * markup divides by that cost (NaN / Infinity when it is 0). New code —
 * the Proposal Builder V2 palette — uses `resolvePartPricing` /
 * `catalogItemToQuoteItemV2` below instead.
 */
export function catalogItemToQuoteItem(
  catalogItem: PartsCatalogItem,
  quantity: number = 1
): PlaygroundQuoteItem {
  const unitCost = (catalogItem.metadata?.unitCost as number) || catalogItem.price || 0;
  const unitPrice = catalogItem.price || 0;
  const markup = (catalogItem.metadata?.markup as number) || ((unitPrice - unitCost) / unitCost);

  return {
    id: crypto.randomUUID(),
    name: catalogItem.name,
    sku: catalogItem.sku,
    partsCatalogId: catalogItem.id,
    category: catalogItem.category,
    quantity,
    unit: (catalogItem.metadata?.unit as string) || 'ea',
    unitCost,
    markup,
    unitPrice,
    totalPrice: unitPrice * quantity,
    sourceType: 'parts_catalog',
    pipelineStatus: 'draft',
    notes: catalogItem.description,
    addedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/** A catalog part's money, resolved once (see `resolvePartPricing`). */
export interface ResolvedPartPricing {
  /** Sell price per unit, ex-GST: `price` ?? `sellPrice` ?? 0. */
  unitPrice: number;
  /** Cost per unit, ex-GST: `costPrice` ?? `metadata.unitCost` ?? 0. */
  unitCost: number;
  /**
   * The part's stored `markup` (ratio, 0.25 = 25%); else
   * (unitPrice − unitCost) / unitCost when the cost is known (> 0); else
   * undefined. Never NaN / Infinity.
   */
  markup: number | undefined;
}

/**
 * THE parts price resolver (Proposal Builder V2, S1 — plan non-negotiable
 * 8). One definition of what a catalog part costs and sells for, so the V2
 * palette never inherits `catalogItemToQuoteItem`'s drift. `price` is the
 * alias most writers set, so it wins over `sellPrice`; cost comes from
 * `costPrice` with the legacy `metadata.unitCost` as the fallback. Every
 * value goes through `toFiniteMoney`, so string-typed numbers (console
 * edits, CSV imports) heal and garbage reads as 0.
 */
export function resolvePartPricing(
  part: Pick<PartsCatalogItem, 'price' | 'sellPrice' | 'costPrice' | 'markup' | 'metadata'>,
): ResolvedPartPricing {
  const unitPrice = toFiniteMoney(part.price ?? part.sellPrice ?? 0) ?? 0;
  const unitCost = toFiniteMoney(part.costPrice ?? part.metadata?.unitCost ?? 0) ?? 0;
  const markup =
    toFiniteMoney(part.markup) ??
    (unitCost > 0 ? roundMoney((unitPrice - unitCost) / unitCost) : undefined);
  return { unitPrice, unitCost, markup };
}

/**
 * `catalogItemToQuoteItem` for Proposal Builder V2: identical shape
 * (`sourceType: 'parts_catalog'`, `pipelineStatus: 'draft'`,
 * `notes = description`, unit defaulting to 'ea'), but the money comes from
 * `resolvePartPricing`. `markup` is omitted when it can't be known.
 */
export function catalogItemToQuoteItemV2(
  catalogItem: PartsCatalogItem,
  quantity: number = 1,
): PlaygroundQuoteItem {
  const { unitPrice, unitCost, markup } = resolvePartPricing(catalogItem);
  const metaUnit = catalogItem.metadata?.unit;
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: catalogItem.name,
    sku: catalogItem.sku,
    partsCatalogId: catalogItem.id,
    category: catalogItem.category,
    quantity,
    unit: catalogItem.unit || (typeof metaUnit === 'string' && metaUnit) || 'ea',
    unitCost,
    ...(markup !== undefined ? { markup } : {}),
    unitPrice,
    totalPrice: roundMoney(unitPrice * quantity),
    sourceType: 'parts_catalog',
    pipelineStatus: 'draft',
    notes: catalogItem.description,
    addedAt: now,
    updatedAt: now,
  };
}

// ============================================================================
// DUPLICATE DETECTION
// ============================================================================

/**
 * Check if a part with same name/SKU exists (duplicate detection)
 * Returns existing part ID if found, null otherwise
 */
export async function findDuplicatePart(
  name: string,
  sku?: string,
  tenantId: string = getTenantIdSync()
): Promise<string | null> {
  // Tenant filter is always required — `parts_catalog` is
  // tenant-isolated. Union-read for legacy-alias compat.
  const tenantsToScan = tenantIdReadUnion(tenantId);

  // Search by name first
  const nameQuery = query(
    collection(db, COLLECTION_NAME),
    where('name', '==', name),
    where('tenantId', 'in', tenantsToScan),
    where('isActive', '==', true),
    firestoreLimit(1)
  );

  const nameSnap = await getDocs(nameQuery);
  if (!nameSnap.empty) {
    return nameSnap.docs[0].id;
  }

  // Search by SKU if provided
  if (sku) {
    const skuQuery = query(
      collection(db, COLLECTION_NAME),
      where('sku', '==', sku),
      where('tenantId', 'in', tenantsToScan),
      where('isActive', '==', true),
      firestoreLimit(1)
    );

    const skuSnap = await getDocs(skuQuery);
    if (!skuSnap.empty) {
      return skuSnap.docs[0].id;
    }
  }

  return null;
}

// ============================================================================
// FAVORITES & LIFECYCLE
// ============================================================================

/**
 * Toggle favorite status on a part
 */
export async function toggleFavorite(partId: string): Promise<boolean> {
  const part = await getPartsCatalogItem(partId);
  if (!part) throw new Error('Part not found');

  const newStatus = !part.isFavorite;
  await updatePartsCatalogItem(partId, { isFavorite: newStatus });
  return newStatus;
}

/**
 * Mark a part as end-of-life
 * EOL parts are hidden from quote builder pickers but remain visible in the catalog
 */
export async function markAsEOL(
  partId: string,
  eolNote?: string,
  replacementId?: string
): Promise<void> {
  await updatePartsCatalogItem(partId, {
    isEOL: true,
    eolNote: eolNote || undefined,
    eolReplacementId: replacementId || undefined,
  });
}

/**
 * Remove EOL status from a part
 */
export async function removeEOL(partId: string): Promise<void> {
  await updatePartsCatalogItem(partId, {
    isEOL: false,
    eolNote: undefined,
    eolReplacementId: undefined,
  });
}

/**
 * Get all favorite parts (not archived, not EOL)
 */
export async function getFavoriteParts(): Promise<PartsCatalogItem[]> {
  const allParts = await getActivePartsCatalogItems();
  return allParts.filter((p) => p.isFavorite && !p.isEOL);
}

// ============================================================================
// SKU LOOKUP — V107.52.36 unified catalog
// ============================================================================

/**
 * Find a single part by SKU (case-insensitive). Returns null on miss.
 *
 * Hot path for the proposal renderer + Better Proposals matcher. SKUs in
 * `parts_catalog` are stored as-typed; we normalise the search term and
 * compare case-insensitively client-side. We pre-filter by uppercase + lowercase
 * variants to keep the Firestore round-trip small.
 */
export async function findPartBySku(sku: string): Promise<PartsCatalogItem | null> {
  const target = normaliseSkuForCompare(sku);
  if (!target) return null;

  // Try exact match against the typed value first (fastest happy path).
  const variants = Array.from(
    new Set([target, target.toLowerCase(), sku.trim()])
  ).filter(Boolean).slice(0, 10); // Firestore `in` cap is 30; 10 covers any reasonable casing variant

  try {
    // Two `in` filters are valid in a single Firestore query (they
    // conjunct) — tenant union-read for legacy-alias safety net,
    // sku for the casing variants.
    const q = query(
      collection(db, COLLECTION_NAME),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('sku', 'in', variants),
      firestoreLimit(5)
    );
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const data = d.data() as Omit<PartsCatalogItem, 'id'>;
      if (normaliseSkuForCompare(data.sku) === target) {
        return { id: d.id, ...data };
      }
    }
  } catch (err) {
    console.warn('[parts-catalog.service] findPartBySku query failed, falling back to scan:', err);
  }

  // Fallback: scan all active parts. Acceptable — the catalog is typically
  // a few hundred items, and this only fires when the indexed query misses.
  const all = await getActivePartsCatalogItems();
  return all.find((p) => normaliseSkuForCompare(p.sku) === target) ?? null;
}

/**
 * Batch-load parts for a list of SKUs. Returns a Map keyed by NORMALISED
 * SKU (uppercase, trimmed) so callers don't have to re-normalise.
 *
 * Used by the magazine renderer to populate the product gallery in a
 * single round-trip. Missing SKUs are absent from the map (no entry =
 * the renderer falls back to a placeholder).
 */
export async function listPartsBySkus(
  skus: string[]
): Promise<Map<string, PartsCatalogItem>> {
  const result = new Map<string, PartsCatalogItem>();
  const targets = Array.from(
    new Set(skus.map(normaliseSkuForCompare).filter(Boolean))
  );
  if (targets.length === 0) return result;

  // Build all reasonable casing variants and chunk into Firestore `in`-friendly batches.
  // We fetch by variant, then filter by normalised match in case the stored SKU has
  // mixed casing the variant set didn't anticipate.
  const variants = new Set<string>();
  for (const t of targets) {
    variants.add(t);
    variants.add(t.toLowerCase());
  }
  // Also pull in the original (un-normalised) SKUs supplied by the caller so we catch
  // anything stored with weird casing/spacing.
  for (const raw of skus) {
    const trimmed = raw.trim();
    if (trimmed) variants.add(trimmed);
  }

  const variantList = Array.from(variants);
  const chunkSize = 30; // Firestore `in` cap
  const chunks: string[][] = [];
  for (let i = 0; i < variantList.length; i += chunkSize) {
    chunks.push(variantList.slice(i, i + chunkSize));
  }

  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        // Two `in` filters conjunct — tenant union-read keeps legacy
        // alias docs visible, sku chunks the typed variants.
        const q = query(
          collection(db, COLLECTION_NAME),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where('sku', 'in', chunk),
        );
        const snap = await getDocs(q);
        for (const d of snap.docs) {
          const data = d.data() as Omit<PartsCatalogItem, 'id'>;
          const norm = normaliseSkuForCompare(data.sku);
          if (norm && targets.includes(norm) && !data.archived) {
            result.set(norm, { id: d.id, ...data });
          }
        }
      } catch (err) {
        console.warn('[parts-catalog.service] listPartsBySkus chunk failed:', err);
      }
    })
  );

  return result;
}

// ============================================================================
// IMAGE UPLOAD — V107.52.36 unified catalog
// ============================================================================

/**
 * Upload a product image for a catalog part. Returns download URL +
 * Storage path. Caller is responsible for writing those values back to
 * the catalog doc via `updatePartsCatalogItem`.
 *
 * Path: `parts-catalog/{tenantId}/{partId}/{timestamp}-{filename}`
 *
 * The Storage path is namespaced by tenantId + partId so admin deletes
 * can clean up the whole prefix in one shot.
 */
export async function uploadPartImage(args: {
  tenantId: string;
  partId: string;
  file: File;
}): Promise<{ downloadUrl: string; storagePath: string }> {
  const ts = Date.now();
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  const path = `parts-catalog/${args.tenantId}/${args.partId}/${ts}-${safeName}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, args.file, {
    contentType: args.file.type || 'image/jpeg',
    customMetadata: { tenantId: args.tenantId, partId: args.partId },
  });
  const downloadUrl = await getDownloadURL(fileRef);
  return { downloadUrl, storagePath: path };
}

/**
 * Upload an ad-hoc product image for a proposal LINE ITEM that isn't backed
 * by a catalog part (e.g. a template's text-defined starter item). Stored
 * under a proposal-item prefix; the caller stamps the returned `downloadUrl`
 * straight onto the quote item's `imageUrl`, so it flows to the customer
 * view + PDF + frozen snapshot exactly like a catalog image. The returned
 * URL is a token-bearing Firebase Storage download URL — publicly readable,
 * which the unauthenticated customer viewer needs.
 *
 * Path: `proposal-item-images/{tenantId}/{itemId}/{timestamp}-{filename}`
 */
export async function uploadInlineProductImage(args: {
  tenantId: string;
  itemId: string;
  file: File;
}): Promise<{ downloadUrl: string; storagePath: string }> {
  const ts = Date.now();
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  const path = `proposal-item-images/${args.tenantId}/${args.itemId}/${ts}-${safeName}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, args.file, {
    contentType: args.file.type || 'image/jpeg',
    customMetadata: { tenantId: args.tenantId, itemId: args.itemId },
  });
  const downloadUrl = await getDownloadURL(fileRef);
  return { downloadUrl, storagePath: path };
}

/**
 * Best-effort delete of an image from Firebase Storage. Failures are
 * swallowed — orphaned files can be reaped by a periodic sweep later.
 * Caller is responsible for clearing `imageUrl`/`imageStoragePath` on
 * the catalog doc.
 */
export async function deletePartImage(storagePath: string): Promise<void> {
  if (!storagePath) return;
  try {
    await deleteObject(storageRef(storage, storagePath));
  } catch (err) {
    console.warn('[parts-catalog.service] deletePartImage failed (orphaned file):', err);
  }
}

// ============================================================================
// BUNDLES — V107.52.36 unified catalog
// ============================================================================

/**
 * Resolve a bundle into its child parts. Returns the parent bundle plus
 * one entry per child with the resolved catalog item + bundle quantity.
 *
 * Throws if the partId doesn't exist or isn't a bundle. Missing children
 * are skipped (logged warning) — a stale bundle reference shouldn't break
 * the whole quote. Bundles do NOT recurse: if a child is itself a bundle,
 * the child is returned as-is (the caller decides whether to recurse).
 * This keeps the surface simple and prevents accidental cycles.
 */
export async function expandBundle(
  partId: string
): Promise<{
  bundle: PartsCatalogItem;
  children: { item: PartsCatalogItem; quantity: number; notes?: string }[];
}> {
  const bundle = await getPartsCatalogItem(partId);
  if (!bundle) throw new Error(`Bundle not found: ${partId}`);
  if (bundle.kind !== 'bundle') {
    throw new Error(`Catalog item is not a bundle (kind=${bundle.kind ?? 'hardware'}): ${partId}`);
  }

  const items = bundle.bundleItems ?? [];
  const children: { item: PartsCatalogItem; quantity: number; notes?: string }[] = [];
  await Promise.all(
    items.map(async (line) => {
      try {
        const child = await getPartsCatalogItem(line.partId);
        if (child && !child.archived) {
          children.push({ item: child, quantity: line.quantity, notes: line.notes });
        } else {
          console.warn(
            `[parts-catalog.service] Bundle ${partId} references missing/archived child ${line.partId} — skipped`
          );
        }
      } catch (err) {
        console.warn(`[parts-catalog.service] Failed to resolve bundle child ${line.partId}:`, err);
      }
    })
  );
  return { bundle, children };
}

/**
 * Convert a bundle into a flat array of `PlaygroundQuoteItem`s for
 * insertion into a quote. Each child becomes one quote line; the bundle
 * itself is not emitted as a separate line. The bundle's id + name are
 * stored on each child line via `metadata.bundleId` / `metadata.bundleName`
 * so the quote builder can group/edit them as a unit.
 *
 * `bundleQuantity` multiplies every child line's quantity (e.g. ordering
 * 2 of a "WiFi 7 Starter Kit" → each child line ×2).
 *
 * Pricing rule (V107.52.36 audit-fix):
 *   - If `bundle.sellPrice` is unset → each child line uses its own
 *     catalog `sellPrice` (sum-from-children, original behaviour).
 *   - If `bundle.sellPrice` IS set → it's treated as the FIXED quote
 *     total for the bundle, scaled by `bundleQuantity`. The total is
 *     distributed proportionally across the children based on their
 *     individual prices, so each line's `unitPrice` is rebased to make
 *     the row totals sum to the bundle's fixed price. If every child
 *     happens to have a $0 catalog price, the bundle total is split
 *     evenly across the lines instead.
 *   - Each emitted line is stamped with `bundleId` + `bundleName` so the
 *     quote builder can group/edit them as a unit.
 */
export async function flattenBundleToQuoteItems(
  partId: string,
  bundleQuantity: number = 1
): Promise<PlaygroundQuoteItem[]> {
  const { bundle, children } = await expandBundle(partId);
  if (children.length === 0) return [];

  // Pre-compute child unit prices so we can rebase if the bundle has a
  // fixed sellPrice. Cost stays per-child; only the customer-facing price
  // is rebased.
  const childUnitPrices = children.map(({ item }) => item.sellPrice ?? item.price ?? 0);
  const childRowTotalsAtCatalogPrice = children.map(
    ({ quantity }, idx) => childUnitPrices[idx] * quantity
  );
  const sumAtCatalogPrice = childRowTotalsAtCatalogPrice.reduce((a, b) => a + b, 0);

  const fixedBundleSell = typeof bundle.sellPrice === 'number' ? bundle.sellPrice : null;
  const useFixedBundlePrice = fixedBundleSell !== null && fixedBundleSell >= 0;
  const fixedBundleTotal = useFixedBundlePrice ? fixedBundleSell! * bundleQuantity : 0;

  return children.map(({ item, quantity, notes }, idx) => {
    const lineQty = quantity * bundleQuantity;
    const unitCost = item.costPrice ?? (item.metadata?.unitCost as number | undefined) ?? 0;

    let unitPrice: number;
    if (useFixedBundlePrice) {
      // Distribute the fixed bundle total across rows. Proportional split when
      // catalog prices are non-zero; even split otherwise.
      const allChildrenZeroPrice = sumAtCatalogPrice <= 0;
      const lineShareTotal = allChildrenZeroPrice
        ? fixedBundleTotal / children.length
        : (childRowTotalsAtCatalogPrice[idx] / sumAtCatalogPrice) * fixedBundleTotal;
      unitPrice = lineQty > 0 ? lineShareTotal / lineQty : 0;
    } else {
      unitPrice = childUnitPrices[idx];
    }

    const markup =
      typeof item.markup === 'number'
        ? item.markup
        : unitCost > 0
        ? (unitPrice - unitCost) / unitCost
        : 0;

    const playgroundItem: PlaygroundQuoteItem = {
      id: crypto.randomUUID(),
      name: item.name,
      sku: item.sku,
      partsCatalogId: item.id,
      category: item.category,
      quantity: lineQty,
      unit: item.unit ?? 'ea',
      unitCost,
      markup,
      unitPrice,
      totalPrice: unitPrice * lineQty,
      sourceType: 'parts_catalog',
      pipelineStatus: 'draft',
      notes: notes ?? item.description,
      addedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      bundleId: bundle.id,
      bundleName: bundle.name,
    };
    return playgroundItem;
  });
}

/**
 * Validate that a `bundleItems[]` array doesn't reference itself (direct
 * cycle protection). Returns an array of error messages — empty when valid.
 *
 * Does NOT walk multi-level cycles (A → B → A); the bundle expander does
 * not recurse so multi-level cycles can't blow up at runtime, but the UI
 * may want to warn anyway. That's a Phase 4 concern.
 */
export function validateBundleItems(
  bundleId: string | undefined,
  items: BundleChildItem[]
): string[] {
  const errors: string[] = [];
  if (!Array.isArray(items)) return ['bundleItems must be an array'];
  if (items.length === 0) return ['Bundles must contain at least one child item'];

  for (let i = 0; i < items.length; i++) {
    const line = items[i];
    if (!line.partId) {
      errors.push(`Line ${i + 1}: missing partId`);
      continue;
    }
    if (bundleId && line.partId === bundleId) {
      errors.push(`Line ${i + 1}: bundle cannot reference itself`);
    }
    if (!Number.isFinite(line.quantity) || line.quantity <= 0) {
      errors.push(`Line ${i + 1}: quantity must be a positive number`);
    }
  }
  return errors;
}

// ============================================================================
// PRODUCT IMAGE LOOKUP — magazine renderer hot path
// ============================================================================

/**
 * Shape returned to the magazine renderer.
 */
export interface CatalogImageRef {
  primaryImageUrl: string;
  whyChosen?: string;
  manufacturer?: string;
}

/**
 * Batch lookup of imagery for SKUs, sourced from the unified catalog.
 * SKUs without an image are absent from the returned map (renderer
 * falls back to a placeholder).
 *
 * Map keys are NORMALISED (uppercase, trimmed) so the renderer's
 * `productImagesBySku.get(sku.toUpperCase())` pattern keeps working.
 */
export async function listCatalogImagesForSkus(
  skus: string[]
): Promise<Map<string, CatalogImageRef>> {
  const parts = await listPartsBySkus(skus);
  const out = new Map<string, CatalogImageRef>();
  for (const [normSku, part] of parts) {
    if (!part.imageUrl) continue; // No image; let renderer fall back
    out.set(normSku, {
      primaryImageUrl: part.imageUrl,
      whyChosen: part.whyChosen,
      manufacturer: part.manufacturer ?? part.brand,
    });
  }
  return out;
}

/**
 * Batch lookup of imagery by parts_catalog DOC ID — the reliable key.
 * Every catalog-sourced quote item carries `partsCatalogId`, whereas
 * `sku` is optional and frequently blank, so an image attached to a
 * sku-less part never reached the proposal under the sku-only lookup.
 *
 * Uses per-id `getDoc` rather than a `documentId() in` query on purpose:
 *   - No composite index needed (a `tenantId in` + `documentId() in`
 *     query may require one and fail at runtime).
 *   - Tenant isolation is enforced by the parts_catalog per-doc read
 *     rule (`resource.data.tenantId == token.tenantId`) — no query-side
 *     filter required.
 * Read count is bounded by the proposal's product count (tens, not
 * thousands). Map keys are the RAW doc id. Parts without an image (or a
 * denied read in an unauthenticated/public render) are simply absent —
 * the renderer falls back to the placeholder, exactly as the sku path
 * already does.
 */
export async function listCatalogImagesForIds(
  ids: string[]
): Promise<Map<string, CatalogImageRef>> {
  const targets = Array.from(new Set(ids.filter(Boolean)));
  const out = new Map<string, CatalogImageRef>();
  if (targets.length === 0) return out;

  await Promise.all(
    targets.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, COLLECTION_NAME, id));
        if (!snap.exists()) return;
        const data = snap.data() as Omit<PartsCatalogItem, 'id'>;
        if (data.archived || !data.imageUrl) return;
        out.set(id, {
          primaryImageUrl: data.imageUrl,
          whyChosen: data.whyChosen,
          manufacturer: data.manufacturer ?? data.brand,
        });
      } catch (err) {
        // Cross-tenant id or denied public read — log + skip so the sku
        // fallback (and the placeholder) still work.
        console.warn('[parts-catalog.service] listCatalogImagesForIds doc failed:', id, err);
      }
    })
  );

  return out;
}

/**
 * One-call image map for the proposal renderer. Resolves imagery by
 * parts_catalog doc id (reliable — always set on catalog items) with a
 * sku fallback for legacy items that carry no catalog id. Returns a
 * SINGLE map whose entries are keyed by RAW partsCatalogId AND by
 * NORMALISED (uppercase) sku; the renderer (`resolveProductImage`)
 * tries id first, then sku. The two namespaces never collide —
 * Firestore ids are mixed-case 20-char tokens, sku keys are upper-cased.
 *
 * To avoid double-reads, sku is only fetched for items WITHOUT a
 * partsCatalogId (an item that has both gets its image from the id
 * lookup of the very same part).
 */
export async function listProductImagesForItems(
  items: ReadonlyArray<{ sku?: string; partsCatalogId?: string }>
): Promise<Map<string, CatalogImageRef>> {
  const ids: string[] = [];
  const skus: string[] = [];
  for (const it of items) {
    if (it.partsCatalogId) ids.push(it.partsCatalogId);
    else if (it.sku) skus.push(it.sku);
  }
  const [bySku, byId] = await Promise.all([
    listCatalogImagesForSkus(skus),
    listCatalogImagesForIds(ids),
  ]);
  const out = new Map<string, CatalogImageRef>();
  for (const [k, v] of bySku) out.set(k, v);
  for (const [k, v] of byId) out.set(k, v);
  return out;
}

